import { Search } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import type { LawyerDTO } from "../../../api/types";

type Props = {
  members: LawyerDTO[];
  value: string[];
  onChange: (ids: string[]) => void;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  getAvatarUrl: (uid: string) => string | undefined;
  currentUserId?: string;
};

const SEARCH_THRESHOLD = 6;

export const lawyerName = (m: LawyerDTO): string =>
  [m.firstName, m.lastName].filter((p) => p?.trim()).join(" ") || m.email;

const initials = (m: LawyerDTO): string =>
  `${m.firstName?.[0] ?? ""}${m.lastName?.[0] ?? ""}`.toUpperCase() ||
  (m.email?.[0] ?? "?").toUpperCase();

export const AssigneePicker: FC<Props> = ({
  members,
  value,
  onChange,
  isLoading,
  error,
  onRetry,
  getAvatarUrl,
  currentUserId
}) => {
  const [query, setQuery] = useState("");
  const selected = useMemo(() => new Set(value), [value]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q
      ? members.filter((m) =>
          `${lawyerName(m)} ${m.email}`.toLowerCase().includes(q)
        )
      : members;
    return [...rows].sort(
      (a, b) =>
        Number(b.uid === currentUserId) - Number(a.uid === currentUserId) ||
        lawyerName(a).localeCompare(lawyerName(b))
    );
  }, [currentUserId, members, query]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-11" />
        <Skeleton className="h-11" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-danger">
        Couldn&apos;t load your team.
        <Button type="button" size="xs" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  if (members.length === 0) {
    return (
      <p className="text-base text-fg-3">
        It&apos;s just you for now; the client will be assigned to you.
      </p>
    );
  }

  const toggle = (uid: string, on: boolean) =>
    onChange(on ? [...value, uid] : value.filter((id) => id !== uid));

  return (
    <div className="flex flex-col gap-2">
      {members.length > SEARCH_THRESHOLD ? (
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-4" />
          <Input
            aria-label="Search team members"
            placeholder="Search team members"
            className="pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      ) : null}
      <ul className="flex max-h-72 flex-col divide-y divide-line-1 overflow-y-auto rounded-lg ring-1 ring-line-2">
        {shown.map((m) => {
          const id = `assignee-${m.uid}`;
          return (
            <li key={m.uid}>
              <label
                htmlFor={id}
                className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-fill-1"
              >
                <Checkbox
                  id={id}
                  checked={selected.has(m.uid)}
                  onCheckedChange={(on) => toggle(m.uid, on === true)}
                />
                <Avatar size="md">
                  <AvatarImage src={getAvatarUrl(m.uid)} alt="" />
                  <AvatarFallback className="text-xs">
                    {initials(m)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base text-fg">
                    {lawyerName(m)}
                    {m.uid === currentUserId ? (
                      <span className="text-fg-3"> (you)</span>
                    ) : null}
                  </span>
                  <span className="truncate text-sm text-fg-3">
                    {m.title || m.email}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
        {shown.length === 0 ? (
          <li className="px-3 py-2 text-base text-fg-3">
            No one matches “{query}”.
          </li>
        ) : null}
      </ul>
    </div>
  );
};
